import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity as ActivityIcon, Filter, RotateCw, Calendar,
  Car, ClipboardList, AlertTriangle, AlertOctagon, ShieldCheck,
  Wallet, Users, MoreHorizontal, FileText, MessageSquare,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/status";

const CATEGORY_META = {
  booking:    { label: "Bookings",    icon: ClipboardList,   color: "bg-blue-100 text-blue-900 border-blue-200" },
  damage:     { label: "Damage",      icon: Car,             color: "bg-amber-100 text-amber-900 border-amber-200" },
  warning:    { label: "Warnings",    icon: AlertTriangle,   color: "bg-rose-100 text-rose-900 border-rose-200" },
  incident:   { label: "Incidents",   icon: AlertOctagon,    color: "bg-orange-100 text-orange-900 border-orange-200" },
  inspection: { label: "Inspections", icon: ShieldCheck,     color: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  deposit:    { label: "Deposits",    icon: Wallet,          color: "bg-violet-100 text-violet-900 border-violet-200" },
  terms:      { label: "Terms",       icon: FileText,        color: "bg-teal-100 text-teal-900 border-teal-200" },
  communication: { label: "Communications", icon: MessageSquare, color: "bg-indigo-100 text-indigo-900 border-indigo-200" },
  vehicle:    { label: "Vehicles",    icon: Car,             color: "bg-slate-100 text-slate-900 border-slate-200" },
  staff:      { label: "Staff",       icon: Users,           color: "bg-cyan-100 text-cyan-900 border-cyan-200" },
  other:      { label: "Other",       icon: MoreHorizontal,  color: "bg-slate-100 text-slate-800 border-slate-200" },
};

function humanAction(t) {
  return (t || "").replace(/_/g, " ");
}

export default function ActivityFeed() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [category, setCategory] = useState("");      // "" = all
  const [since, setSince] = useState("");            // YYYY-MM-DD
  const [until, setUntil] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (category) params.category = category;
      if (since) params.since = `${since}T00:00:00+00:00`;
      if (until) params.until = `${until}T23:59:59+00:00`;
      const [{ data: feed }, { data: c }] = await Promise.all([
        api.get("/activity", { params }),
        api.get("/activity/counts", { params: { days: 7 } }),
      ]);
      setItems(feed.items || []);
      setCounts(c);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [category, since, until]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    // Group by day for the timeline view
    const byDay = new Map();
    for (const it of items) {
      const day = (it.created_at || "").slice(0, 10);
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(it);
    }
    return Array.from(byDay.entries());
  }, [items]);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="activity-feed-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Business activity</div>
          <h1 className="font-display text-3xl font-bold mt-1">Activity feed</h1>
          <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
            A chronological view of bookings, damages, warnings, inspections, deposits and staff actions across your company.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="activity-refresh-btn"
          className="px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1"
        >
          <RotateCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Category filter chips */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="activity-filters">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          <Filter className="w-3.5 h-3.5" /> Filter
        </div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip active={category === ""} onClick={() => setCategory("")} testId="activity-cat-all">All</FilterChip>
          {Object.entries(CATEGORY_META).filter(([k]) => k !== "other").map(([key, meta]) => {
            const count = counts?.by_category?.[key] || 0;
            const Icon = meta.icon;
            return (
              <FilterChip
                key={key}
                active={category === key}
                onClick={() => setCategory(key)}
                testId={`activity-cat-${key}`}
              >
                <Icon className="w-3 h-3 mr-1 inline" /> {meta.label}
                {count > 0 && <span className="ml-1 text-[10px] text-slate-500">({count})</span>}
              </FilterChip>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-xs text-slate-600">
            <span className="block mb-1">From</span>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              data-testid="activity-filter-since"
              className="px-2 py-1.5 rounded-md border border-slate-300 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="block mb-1">To</span>
            <input
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              data-testid="activity-filter-until"
              className="px-2 py-1.5 rounded-md border border-slate-300 text-sm"
            />
          </label>
          {(since || until || category) && (
            <button
              onClick={() => { setSince(""); setUntil(""); setCategory(""); }}
              data-testid="activity-filter-clear"
              className="px-3 py-1.5 rounded-md text-xs text-slate-700 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-sm text-slate-500" data-testid="activity-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="activity-empty">
          <ActivityIcon className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">
            No activity matches your filters yet.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="activity-timeline">
          {grouped.map(([day, dayItems]) => (
            <section key={day} data-testid={`activity-day-${day}`}>
              <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                <Calendar className="w-3.5 h-3.5" /> {fmtDate(day) || day}
                <span className="text-slate-400 normal-case font-normal">· {dayItems.length} event{dayItems.length === 1 ? "" : "s"}</span>
              </div>
              <ul className="space-y-2">
                {dayItems.map((it) => <ActivityItem key={it.id} item={it} />)}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active
          ? "bg-[#1B4332] text-white border-[#1B4332]"
          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function ActivityItem({ item }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
  const Icon = meta.icon;
  const time = (item.created_at || "").slice(11, 16);
  // Build a "View" link only when we have a real reference id
  let viewLink = null;
  if (item.booking_id) viewLink = `/app/bookings/${item.booking_id}`;
  else if (item.item_id) viewLink = `/app/vehicles/${item.item_id}`;
  else if (item.incident_id) viewLink = `/app/incidents/${item.incident_id}`;

  return (
    <li
      className="rounded-lg border border-slate-200 bg-white px-4 py-3 flex items-start gap-3"
      data-testid={`activity-item-${item.id}`}
    >
      <div className={`w-8 h-8 rounded-md grid place-items-center border ${meta.color} flex-shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold border ${meta.color}`}>
            {meta.label}
          </span>
          <span className="text-xs font-mono text-slate-500">{humanAction(item.action_type)}</span>
          <span className="text-xs text-slate-400">· {time}</span>
        </div>
        <p className="text-sm text-slate-800 mt-0.5">{item.description}</p>
        <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
          {item.user_name && <span>By {item.user_name}</span>}
          {viewLink && (
            <Link
              to={viewLink}
              data-testid={`activity-item-view-${item.id}`}
              className="text-[#1B4332] hover:underline font-medium"
            >
              View →
            </Link>
          )}
        </div>
      </div>
    </li>
  );
}
